import { useToolStore } from '../store/useToolStore';
import { PROMPT_MANIFEST } from '../config/promptManifest';

// Helper to find the closest match in a "range" map
const resolveRange = (value: number, map: Record<number, string>) => {
    const sortedKeys = Object.keys(map).map(Number).sort((a, b) => a - b);
    for (const key of sortedKeys) {
        if (value <= key) return map[key];
    }
    return map[sortedKeys[sortedKeys.length - 1]];
};

// Helper to look up values in "lookup" map
const resolveLookup = (value: string | string[], map: Record<string, string>) => {
    if (Array.isArray(value)) {
        return value.map(v => map[v] || v).join(" ");
    }
    return map[value] || value;
};

// Helper to traverse dot notation state (e.g. "lensParams.entourage.crowdDensity")
const getValueFromPath = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export const synthesizePrompt = (userPrompt: string, overrideValues: Record<string, any> = {}): string => {
    const state = useToolStore.getState();
    const { activeTool, landTypology, landSettings, materialOverrideParams } = state;

    // 1. Get Manifest for current tool
    const toolManifest = PROMPT_MANIFEST[activeTool as keyof typeof PROMPT_MANIFEST];

    // If no manifest (e.g. 'gallery'), fallback
    if (!toolManifest) {
        return `You are an expert architectural AI. ${userPrompt}`;
    }

    const { role, modifiers, context_templates } = toolManifest as any; // Cast to any to bypass strict union mismatch on context_templates
    let builtPrompt = `${role}\n\n`;

    // 2. Add "Land Typology" Context if applicable (Render Bridge mainly)
    if (activeTool === 'render-bridge' && context_templates) {
        const typoContext = context_templates[landTypology as keyof typeof context_templates];
        if (typoContext) {
            builtPrompt += `[-- LAND TYPOLOGY & LAYOUT --]\n${typoContext}\n`;
            // Add dynamic values from landSettings? 
            // For now, the text "High density" etc in manifest is static, 
            // but we can append specific numbers if we want strictly accurate physics.
            if (landTypology === 'garden') {
                builtPrompt += `Lawn Area coverage: ${landSettings.lawnArea}%. Hardscape/Patio Proportion: ${landSettings.patioSize}%. Shrub and Flora Density: ${landSettings.shrubDensity}%.\n`;
            } else {
                builtPrompt += `Greenery: ${landSettings.greeneryDensity}%. Water: ${landSettings.waterFeatures}%.\n`;
            }
        }
    }

    // 2b. Add Lens Location Context
    if (activeTool === 'lens' && state.lensParams.location) {
        builtPrompt += `[-- GEOGRAPHICAL GROUNDING --]\nLocation: ${state.lensParams.location}. Ensure the environment, background, flora, lighting, and entourage strictly match the realistic context and atmosphere of this specific geographical location.\n`;
    }

    // 3. Iterate through Define Modifiers in Manifest
    builtPrompt += `\n[-- ATMOSPHERE & VISUAL ATTRIBUTES --]\n`;

    // We map keys of modifiers (e.g. "timeOfDay") to the actual state path
    // NOTE: The manifest keys I defined in step 1 are shorthand or partial paths.
    // I need to map them to the full store path OR update the manifest keys to match store paths.
    // Let's assume specific mapping based on tool for now to keep manifest clean.

    const modifierKeys = Object.keys(modifiers);

    modifierKeys.forEach(key => {
        const modConfig = modifiers[key as keyof typeof modifiers];
        let value: any;

        // PATH MAPPING STRATEGY:
        // We need to find the value in the store. 
        // 'timeOfDay' -> state.renderBridgeParams.timeOfDay
        // 'camera.viewpoints' -> state.lensParams.camera.viewpoints

        // Let's construct the full path dynamically
        let fullPath = '';
        if (activeTool === 'render-bridge') fullPath = `renderBridgeParams.${key}`;
        else if (activeTool === 'lens') fullPath = `lensParams.${key}`;
        else if (activeTool === 'magic-touch') fullPath = `planToViewParams.${key}`;
        else if (activeTool === 'refiner') fullPath = `refinerParams.${key}`;
        else if (activeTool === 'interior') fullPath = `interiorParams.${key}`;

        // Handle special cases (like flattened keys in manifest vs nested in store if mismatch exists)
        // But my manifest keys align with the store structure (except the prefix).

        // PRIORITY CHECK: Check overrides first!
        if (fullPath in overrideValues) {
            value = overrideValues[fullPath];
        } else {
            value = getValueFromPath(state, fullPath);
        }

        if (value !== undefined && value !== null) {
            let text = "";
            if (modConfig.type === 'range') {
                text = resolveRange(Number(value), modConfig.map);
            } else if (modConfig.type === 'lookup') {
                text = resolveLookup(value, modConfig.map);
            }

            if (text) {
                builtPrompt += `- ${text}\n`;
            }
        }
    });

    // 4. Material Override
    if (materialOverrideParams.textureUrl) {
        builtPrompt += `\n[-- MATERIAL OVERRIDE --]\nApply the custom provided texture to the selected area. Scale: ${materialOverrideParams.textureScale}x.\n`;
    }

    // 4b. Refiner Context & Constraints
    if (activeTool === 'refiner') {
        const { imageMode, action, modifySelectionOnly } = state.refinerParams;

        builtPrompt += `\n[-- TARGET & SCOPE --]\n`;
        builtPrompt += `Target Type: ${imageMode}. Treat the image geometry and context appropriately for this view type.\n`;

        if (action === 'Remove Object') {
            builtPrompt += `Action: REMOVE the selected object and seamlessly hallucinate/fill the background behind it based on the surrounding context.\n`;
        } else if (action === 'Modify/Replace Object') {
            builtPrompt += `Action: REPLACE or MODIFY the selected object according to the user instruction.\n`;
        } else if (action === 'Material Override') {
            builtPrompt += `Action: CHANGE MATERIAL of the selected area.\n`;
        }

        if (modifySelectionOnly) {
            builtPrompt += `Scope: STRICTLY limit all modifications to the boundary of the provided mask/selection. The rest of the image MUST remain physically identical.\n`;
        } else {
            builtPrompt += `Scope: Modify the selected area, but you may subtly harmonize the surrounding lighting/shadows to ensure the new element blends perfectly into the scene.\n`;
        }
    }

    // 4c. Interior Design Context & Constraints
    if (activeTool === 'interior') {
        const { theme, paletteId, timeOfDay, roomType } = state.interiorParams;

        const ROOM_LABELS: Record<string, string> = {
            living_room: 'Living Room',
            bedroom: 'Bedroom',
            kitchen: 'Kitchen & Dining',
            office: 'Office / Study',
            bathroom: 'Bathroom',
        };

        const PALETTE_LABELS: Record<string, string> = {
            ip1: 'Modern Neutrals (White, Light Gray, Steel, Black)',
            ip2: 'Classic Elegance (Dark Wood, Marble, Gold, Crimson)',
            ip3: 'Coastal Breeze (Navy, Sky Blue, White, Teal)',
            ip4: 'Earth & Stone (Dark Brown, Terracotta, Wheat)',
            ip5: 'Urban Industrial (Charcoal, Amber, Black Steel)',
            ip6: 'Scandi Minimal (Pale Gray, Warm White, Greige)',
        };

        builtPrompt += `\n[-- INTERIOR DESIGN CONTEXT --]\n`;
        builtPrompt += `Room Type: ${ROOM_LABELS[roomType] || roomType}. Design for this specific room function and scale.\n`;
        builtPrompt += `Design Theme: ${theme.charAt(0).toUpperCase() + theme.slice(1)}. Apply its characteristic furniture styles, material choices, and ornamentation accordingly.\n`;
        builtPrompt += `Color Palette: ${PALETTE_LABELS[paletteId] || paletteId}. Use these tones consistently across walls, furnishings, and accents.\n`;
        builtPrompt += `Lighting: ${timeOfDay} lighting conditions. Simulate the quality and color temperature of light that matches this time: Day = bright neutral daylight, Sunset = warm orange-gold tones, Night = warm artificial light with cozy pools of illumination.\n`;
    }

    // 5. User Input
    builtPrompt += `\n[-- USER INSTRUCTION --]\n"${userPrompt}"`;

    // 6. Mandatory Negative Constraints
    if (activeTool === 'render-bridge') {
        builtPrompt += `\n\n[-- MANDATORY NEGATIVE CONSTRAINTS --]\nAVOID these elements: low-poly trees, plastic grass, oversaturated greens, indoor furniture placed outdoors, floating plants, blurred textures, unrealistic scale, changing existing architectural walls, extending building footprint.`;
    } else if (activeTool === 'lens') {
        builtPrompt += `\n\n[-- MANDATORY NEGATIVE CONSTRAINTS --]\nAVOID these elements: blurry, distorted, impossible architecture, bad geometry, floating people, incorrect shadows, watermark, structural modifications to the primary building.`;
    } else if (activeTool === 'interior') {
        builtPrompt += `\n\n[-- MANDATORY NEGATIVE CONSTRAINTS --]\nAVOID these elements: cartoon furniture, unrealistic proportions, floating/clipping objects, walls changing shape, incorrect perspective, wrong scale, text artifacts, extra doors or windows not in the original image.`;
    }

    return builtPrompt;
};
